import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { AppLanguage } from '../types/Language';

interface UseLanguagesOptions {
  includeInactive?: boolean;
}

export function sortLanguages(languages: AppLanguage[]): AppLanguage[] {
  return [...languages].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

export function useLanguages(options: UseLanguagesOptions = {}) {
  const includeInactive = options.includeInactive ?? false;

  const query = useQuery<AppLanguage[]>({
    queryKey: ['languages', includeInactive ? 'all' : 'active'],
    queryFn: async () => {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/languages`, {
        params: { includeInactive },
      });
      return response.data as AppLanguage[];
    },
  });

  const languages = useMemo(() => sortLanguages(query.data ?? []), [query.data]);

  const activeLanguages = useMemo(
    () => languages.filter((language) => language.isActive !== false),
    [languages]
  );

  const languageCodes = useMemo(
    () => activeLanguages.map((language) => language.code),
    [activeLanguages]
  );

  const requiredLanguages = useMemo(
    () => activeLanguages.filter((language) => language.isRequired !== false),
    [activeLanguages]
  );

  return {
    ...query,
    languages,
    activeLanguages,
    languageCodes,
    requiredLanguages,
  };
}
